import { showError, showSuccess, useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";

import { useTl } from ".";
import { useDialog } from "../contexts";
import { ETLCodes } from "../locales";
import { ErrorDetails } from "../api";
import { FormikProps } from "formik";

export interface IUseCrudApiOptions<T> {
  getApiFn: (id?: number) => Promise<T> | T;
  saveApiFn: (data: T) => Promise<any> | any;
  deleteApiFn?: (data: T) => Promise<any> | any;
  onSavedRoute?: (data: T) => string;
  onDeletedRoute?: (data: T) => string;
  successMessage?: string;
  deletedMessage?: string;
}

export function useCrudApi<T>(options: IUseCrudApiOptions<T>, dependencies?: React.DependencyList) {
  const { getApiFn, saveApiFn, deleteApiFn, onSavedRoute, onDeletedRoute, successMessage, deletedMessage } = options;
  const { t, tUnsafe } = useTl();
  const { showDialog } = useDialog();
  const history = useHistory();
  const [data, loading, , refresh, setData] = useApiEffect<T>(getApiFn, dependencies);

  const [saving, setSaving] = React.useState(false);

  const finalSuccessMessage = React.useMemo(() => successMessage || t(ETLCodes.SaveSuccess), [successMessage, t]);

  const [saveError, setCurrentError] = React.useState<ErrorDetails>();

  const manageError = React.useCallback(
    (error: any) => {
      console.log(error);
      const details: ErrorDetails = JSON.parse(error.response);
      if (details?.errorCode) {
        if (details?.errorCode === "NavError") {
          showSuccess(t(ETLCodes.SaveSuccess));
          refresh();
        }
        setCurrentError(details);
      } else {
        showError(t(ETLCodes.Error));
      }
    },
    [refresh, t]
  );

  React.useEffect(() => {
    if (saveError && saveError.errorCode) {
      showError(tUnsafe(`BackendError_${saveError.errorCode}`));
    }
  }, [saveError, tUnsafe]);

  const saveItem = React.useCallback(
    async (values: T) => {
      try {
        setSaving(true);
        const savedIdOrDto: any = await saveApiFn(values);
        let savedData: T;

        if (Number.isInteger(savedIdOrDto)) {
          savedData = await getApiFn(+savedIdOrDto);
        } else {
          savedData = savedIdOrDto as T;
        }
        if (savedData) {
          setData(savedData);

          // setCurrentError(null);
          showSuccess(finalSuccessMessage);
          setTimeout(async () => {
            if (onSavedRoute) {
              history.push(onSavedRoute(savedData));
            }
          }, 1);
        }
      } catch (error) {
        manageError(error);
      }
      setSaving(false);
    },
    [finalSuccessMessage, getApiFn, history, manageError, onSavedRoute, saveApiFn, setData]
  );

  const finalDeleteMessage = React.useMemo(() => deletedMessage || t(ETLCodes.DeleteSuccess), [deletedMessage, t]);

  const [deleting, setDeleting] = React.useState(false);
  const deleteItem = React.useCallback(
    (formik?: FormikProps<any>) => {
      showDialog({
        message: t(ETLCodes.DeleteConfirmationMessage),
        title: t(ETLCodes.DeleteDialogTitle),
        onConfirmed: async () => {
          try {
            setDeleting(true);
            await deleteApiFn(data);
            setTimeout(() => {
              if (onDeletedRoute) {
                if (formik) {
                  formik.resetForm();
                }
                history.push(onDeletedRoute(data));
              }
            }, 0);
            showSuccess(finalDeleteMessage);
          } catch (error) {
            manageError(error);
          }
          setDeleting(false);
        },
      });
    },
    [data, deleteApiFn, finalDeleteMessage, history, manageError, onDeletedRoute, showDialog, t]
  );

  return {
    data,
    loading,
    setData,
    saveItem,
    saving,
    deleteItem,
    deleting,
    refresh,
    // saveError
  };
}
